/* NPM */
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
/* UTILS */
import t from "../../../utils/translate";
import { setPageData, signupPageView } from "../../../utils/analytics";
import routes from "../../../routes";
import { COMPOUND, TREATMENT, BUNDLE_ID } from "../constants";
import useAppContext from "../../AppContext";
import { useFetchClinicalEligibility } from "../../../utils/assessment";
import { useCompoundEligibility } from "utils/useCompoundEligibility";
/* COMPONENTS */
import Column from "../../Column";
import SequenceFooter from "../../SequenceFooter";
import RoundButton from "../../RoundButton";
import SiteWrapper from "../../SiteWrapper";
import Cards from "./Cards";
import StickyFooter from "../../StickyFooter";
/* STYLES */
import styles from "./styles.module.scss";

const TreatmentOptions = () => {
  const { newQuiz, contact, profile } = useAppContext();
  const { compoundEligible } = useCompoundEligibility();
  const { market: locale } = contact;
  const { isB2B, isIAP, isRegistered } = profile;
  const history = useHistory();
  let { data: clinicalEligibility, isLoading: clinicalEligibilityLoading } =
    useFetchClinicalEligibility(locale);
  const isUni3or4Quiz = newQuiz.isUni3 || newQuiz.isUni4;

  const [selectedTreatment, setSelectedTreatment] = useState(COMPOUND);

  useEffect(() => {
    setPageData({
      page_category: "clinic",
      site_section: "clinical",
      signup_program: "clinical"
    });
    signupPageView("treatment_options");
  }, []);

  // Redirect to next plans selection page if not compound eligible or B2B.
  useEffect(() => {
    if (!clinicalEligibilityLoading && (!compoundEligible || isB2B)) {
      continueToPlans(true);
    }
  }, [compoundEligible, isB2B, clinicalEligibilityLoading]);

  const continueToPlans = (noTreatment = false) => {
    let bundleID;
    const treatment = noTreatment ? null : selectedTreatment;
    const isActiveUser = isIAP === false && isRegistered === false;
    const { clinicOfferBundleId, compoundedOfferBundleId } =
      clinicalEligibility || {};

    const specialBundleId = sessionStorage.getItem("sbi");
    const specialCompoundingBundleId = sessionStorage.getItem("scbi");

    if (treatment === COMPOUND) {
      // Active core/premium users (lapsed or not)
      // should use the compoundedOfferBundleId from the SMS eligibility endpoint
      if (isActiveUser && compoundedOfferBundleId) {
        bundleID = compoundedOfferBundleId;
      }
      // If a special bundle ID is set, registered/IAP (lapsed or not) users should use it
      else if (specialCompoundingBundleId) {
        bundleID = specialCompoundingBundleId;
        // Lapsed and non-lapsed registered/IAP users should see the same offer
      } else {
        bundleID = t("JS_CLINIC_COMPOUND_BUNDLE_ID");
      }
    }
    // Active core/premium users (lapsed or not) should use the clinicOfferBundleId
    // from the SMS eligibility endpoint
    else if (isActiveUser) {
      bundleID =
        clinicOfferBundleId || t("DEFAULT_FIRST_MONTH_FREE_BUNDLE_ELI_APP");
    }
    // If a special bundle ID is set, registered/IAP (lapsed or not) users should use it
    else if (specialBundleId) {
      bundleID = specialBundleId;
    }
    // If a bundle ID it not specifcially set, fall back to default (empty bundle id param).
    // This should happen for registered/IAP users (lapsed or not).

    const urlParams = new URLSearchParams();
    if (treatment) {
      urlParams.append(TREATMENT, treatment);
    }
    if (bundleID) {
      urlParams.append(BUNDLE_ID, bundleID);
    }
    const paramString = urlParams.toString();

    history.push(
      `${routes.clinicCheckoutPlans}${paramString ? `?${paramString}` : ""}`
    );
  };

  if (clinicalEligibilityLoading) {
    return null;
  }

  const renderFooter = () => {
    if (isUni3or4Quiz)
      return (
        <StickyFooter
          className={styles.unifooter}
          nextButtonProps={{
            onNext: continueToPlans,
            label: t("NEXT"),
            "da-category": "compound_checkout",
            "da-label": `treatment_options:${selectedTreatment}`,
            "da-action": "next"
          }}
        />
      );
    else
      return (
        <SequenceFooter className={styles.footer}>
          <RoundButton
            da-category="compound_checkout"
            da-label={`treatment_options:${selectedTreatment}`}
            da-action="next"
            onClick={continueToPlans}
          >
            {t("NEXT")}
          </RoundButton>
        </SequenceFooter>
      );
  };

  return (
    <SiteWrapper size="small">
      <Column className={styles.content}>
        <h1 className={styles.title}>{t("COMPOUND_CHECKOUT_PAGE_TITLE")}</h1>
        <p className={styles.subtitle}>
          {t("COMPOUND_CHECKOUT_PAGE_SUBTITLE")}
        </p>
        <Cards
          selectedTreatment={selectedTreatment}
          setSelectedTreatment={setSelectedTreatment}
        />
        <div className={styles.disclaimer}>{t("COMPOUNDED_GLP1_FOOTNOTE")}</div>
        {renderFooter()}
      </Column>
    </SiteWrapper>
  );
};

export default TreatmentOptions;
